import { markInputRule, markPasteRule } from '@tiptap/core';
import Bold from '@tiptap/extension-bold';
import { Plugin, PluginKey } from 'prosemirror-state';
import { MarkdownMarkSpec } from 'tiptap-markdown';
import { BoldToken } from '@scale/shared/types/config';

/**
 * Matches bold text via `**` as input.
 */
export const starInputRegex = /(?:^|\s)(\*\*(?!\s+\*\*)((?:[^*]+))\*\*(?!\s+\*\*))$/;

/**
 * Matches bold text via `**` while pasting.
 */
export const starPasteRegex = /(?:^|\s)(\*\*(?!\s+\*\*)((?:[^*]+))\*\*(?!\s+\*\*))/g;

/**
 * Matches bold text via `__` as input.
 */
export const underscoreInputRegex = /(?:^|\s)(__(?!\s+__)((?:[^_]+))__(?!\s+__))$/;

/**
 * Matches bold text via `__` while pasting.
 */
export const underscorePasteRegex = /(?:^|\s)(__(?!\s+__)((?:[^_]+))__(?!\s+__))/g;

/**
 * Matches punctuation text at the start only, excluding quotes
 */
export const startPunctuationRegex = /^(?!["'])\p{P}+/gu;

export const preventSpacelessPunctuation = (name: string) =>
  new Plugin({
    key: new PluginKey(`${name}-spaceless-punctuation`),
    appendTransaction: (_transactions, _oldState, newState) => {
      let tr = newState.tr;
      let modified = false;

      const insertions: { pos: number; text: string }[] = [];

      // Iterate over the document to find bold marks
      newState.doc.descendants((node, pos) => {
        if (node.isText) {
          node.marks.forEach(mark => {
            if (mark.type.name === name) {
              const text = node.text;

              // Checks if text ends with any character that is NOT:
              // \p{L} - a letter (any language)
              // \p{N} - a number
              // \s - whitespace
              // $ - matches end of string
              // /u flag enables Unicode mode
              const endsWithPunctuation = /[^\p{L}\p{N}\s]$/u.test(text ?? '');
              if (endsWithPunctuation) {
                const nextCharPos = pos + node.nodeSize;

                // Check the next character after the bold mark
                const nextChar = newState.doc.textBetween(nextCharPos, nextCharPos + 1, null, '');

                // If the next character is not a space, insert one
                if (nextChar && !/\s/.test(nextChar)) {
                  insertions.push({ pos: nextCharPos, text: ' ' });
                  modified = true;
                }
              }
            }
          });
        }
      });

      // Apply insertions in reverse to avoid shifting positions
      insertions.reverse().forEach(({ text, pos }) => {
        tr = tr.insertText(text, pos);
      });

      // Return a new transaction if any changes were made
      return modified ? tr : null;
    },
  });

export const preventStartingWithPunctuation = (name: string) =>
  new Plugin({
    key: new PluginKey(`${name}-prevent-starting-with-punctuation`),
    appendTransaction(_transactions, _oldState, newState) {
      let tr = newState.tr;
      let modified = false;

      // Traverse all nodes in the document
      newState.doc.descendants((node, pos) => {
        if (node.isText) {
          node.marks.forEach(mark => {
            if (mark.type.name === name) {
              // Adjust the bold mark to exclude the leading punctuation
              const text = node.text ?? '';
              const punctuationMatch = text.match(startPunctuationRegex);
              if (punctuationMatch) {
                const punctuation = punctuationMatch[0];
                const newText = text.slice(punctuation.length); // Remove leading punctuation

                // Replace the original node with a split version: punctuation (no bold) + rest (bold)
                const from = pos;
                const to = pos + node.nodeSize;

                tr = tr
                  .delete(from, to) // Remove old text
                  .insert(from, newState.schema.text(punctuation)); // Add unbolded punctuation

                if (punctuation.length !== node.nodeSize) {
                  tr = tr.insert(from + punctuation.length, newState.schema.text(newText, [mark])); // Add bolded text
                }

                modified = true;
              }
            }
          });
        }
      });

      return modified ? tr : null;
    },
  });

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    bold_extended: {
      toggleBoldWithDefaultToken: () => ReturnType;
    };
  }
}

function getToken(tokenEnum: BoldToken) {
  return tokenEnum;
}

export default Bold.extend({
  addStorage(): { markdown: MarkdownMarkSpec } {
    return {
      markdown: {
        // Source: https://github.com/ProseMirror/prosemirror-markdown/blob/master/src/to_markdown.ts#L134
        serialize: {
          open: (_state, mark) => {
            return mark.attrs.token;
          },
          close: (_state, mark) => {
            return mark.attrs.token;
          },
          mixable: true,
          expelEnclosingWhitespace: true,
        },
        parse: {
          setup(markdownit) {
            markdownit.renderer.rules.strong_open = (tokens, idx, options, _env, self) => {
              // Get the token being processed
              const token = tokens[idx];

              // Check the delimiter type used (token has '__' or '**')
              const markup = token.markup; // `**` or `__`

              // Add a custom attribute based on the markup used
              token.attrSet('token', markup);

              // Pass token to default renderer with the custom attribute
              return self.renderToken(tokens, idx, options);
            };
          },
        },
      },
    };
  },
  addCommands() {
    return {
      ...this.parent?.(),
      toggleBoldWithDefaultToken:
        () =>
        ({ commands }) => {
          const defaultBoldToken =
            this.editor.storage.markdownExtensions.boldToken ?? BoldToken.ASTERISK;
          commands.toggleMark(this.name, { token: getToken(defaultBoldToken) });
          return true;
        },
    };
  },
  addAttributes() {
    return {
      token: {
        default: '**',
        renderHTML: attributes => {
          return {
            token: attributes.token,
          };
        },
      },
    };
  },

  addInputRules() {
    return [
      markInputRule({
        find: starInputRegex,
        type: this.type,
        getAttributes() {
          return { token: '**' };
        },
      }),
      markInputRule({
        find: underscoreInputRegex,
        type: this.type,
        getAttributes() {
          return { token: '__' };
        },
      }),
    ];
  },

  addPasteRules() {
    return [
      markPasteRule({
        find: starPasteRegex,
        type: this.type,
        getAttributes() {
          return { token: '**' };
        },
      }),
      markPasteRule({
        find: underscorePasteRegex,
        type: this.type,
        getAttributes() {
          return { token: '__' };
        },
      }),
    ];
  },
  addProseMirrorPlugins() {
    return [preventSpacelessPunctuation('bold'), preventStartingWithPunctuation('bold')];
  },
});
